import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { useTheme } from 'next-themes';
import { Moon, ArrowLeft, Loader2 } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { z } from 'zod';
import logo from '@/assets/logo.gif';

const DEPARTMENTS = ['Business Development', 'Consulting', 'Strategy', 'Events', 'Ops', 'Leadership'] as const;

const PENDING_SIGNUP_KEY = 'cosmo_pending_signup';

const signupSchema = z.object({
  firstName: z.string().trim().min(1, 'First name is required').max(50, 'First name too long'),
  lastName: z.string().trim().min(1, 'Last name is required').max(50, 'Last name too long'),
  email: z.string().trim().email('Please enter a valid email address').max(255, 'Email too long'),
  password: z.string().min(6, 'Password must be at least 6 characters').max(72, 'Password too long'),
});

export default function Signup() {
  const { theme, setTheme } = useTheme();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const {
    signUp,
    completeGoogleSignup,
    user,
    membership,
    onboardingCompleted,
    loading,
    refreshMembership
  } = useAuth();

  // Step 1: invite code + department
  const [inviteCode, setInviteCode] = useState(searchParams.get('inviteCode') || '');
  const [department, setDepartment] = useState<string>('');
  const [validatingCode, setValidatingCode] = useState(false);

  // Step 2 state: org info from validated invite code
  const [orgInfo, setOrgInfo] = useState<{ org_id: string; org_name: string } | null>(null);

  // Step 2: email/password fields
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);

  // Track whether we're completing a Google signup return
  const [completingGoogle, setCompletingGoogle] = useState(false);
  const googleCompleteAttempted = useRef(false);

  // Handle Google OAuth return — triggered by ?complete=google OR by detecting
  // a signed-in user with pending signup data in localStorage (fallback when
  // Supabase redirects to the site URL instead of the specified redirectTo,
  // which drops the ?complete=google param).
  useEffect(() => {
    if (googleCompleteAttempted.current) return;
    if (loading) return;
    if (!user) return;

    const isGoogleReturn = searchParams.get('complete') === 'google';
    const pendingRaw = localStorage.getItem(PENDING_SIGNUP_KEY);
    const isGoogleUser = user.app_metadata?.provider === 'google';

    // Primary trigger: explicit return param. Fallback: Google-authed user
    // with pending signup data (the ?complete=google param was lost when
    // Supabase redirected to the site URL instead of the specified redirectTo).
    if (!isGoogleReturn && !(isGoogleUser && pendingRaw)) return;

    googleCompleteAttempted.current = true;

    if (!pendingRaw) {
      console.warn('[Signup] Google return detected but no pending signup data in localStorage');
      toast.error('Signup session expired. Please try again.');
      return;
    }

    console.log('[Signup] Completing Google signup', { isGoogleReturn, hasPending: !!pendingRaw });
    const pending = JSON.parse(pendingRaw) as { org_id: string; department: string };
    setCompletingGoogle(true);

    (async () => {
      const { error } = await completeGoogleSignup(pending.org_id, pending.department);
      localStorage.removeItem(PENDING_SIGNUP_KEY);

      if (error) {
        console.error('[Signup] completeGoogleSignup failed:', error);
        toast.error(error.message);
        setCompletingGoogle(false);
      } else {
        console.log('[Signup] Google signup completed, navigating to onboarding');
        toast.success('Account created! Starting onboarding...');
        navigate('/onboarding', { replace: true });
      }
    })();
  }, [searchParams, user, loading, completeGoogleSignup, navigate]);

  // Redirect if already logged in (and not mid-Google-completion)
  useEffect(() => {
    if (completingGoogle) return;
    if (!loading && user && membership) {
      if (onboardingCompleted) {
        navigate('/prompt', { replace: true });
      } else {
        navigate('/onboarding', { replace: true });
      }
    }
  }, [user, membership, onboardingCompleted, loading, navigate, completingGoogle]);

  // Step 1: Validate invite code
  const handleValidateCode = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!inviteCode.trim()) {
      toast.error('Invite code is required');
      return;
    }
    if (!department) {
      toast.error('Please select a department');
      return;
    }

    setValidatingCode(true);
    try {
      const { data: orgData, error: orgError } = await supabase.rpc(
        'lookup_org_by_invite_code',
        { _invite_code: inviteCode.trim() }
      );

      if (orgError) {
        console.error('Invite code lookup error:', orgError);
        toast.error('Invalid invite code. Please check and try again.');
        return;
      }

      const org = orgData && orgData.length > 0 ? orgData[0] : null;
      if (!org) {
        toast.error('Invalid invite code. Please check and try again.');
        return;
      }

      setOrgInfo({ org_id: org.org_id, org_name: org.org_name });
    } catch {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setValidatingCode(false);
    }
  };

  // Step 2: Email/password signup
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!orgInfo) return;

    const result = signupSchema.safeParse({ firstName, lastName, email, password });
    if (!result.success) {
      toast.error(result.error.errors[0].message);
      return;
    }

    setIsLoading(true);
    const { error } = await signUp(email, password, firstName, lastName, inviteCode, department);
    setIsLoading(false);

    if (error) {
      if (error.message.includes('already registered')) {
        toast.error('This email is already registered. Please sign in instead.');
      } else {
        toast.error(error.message);
      }
    } else {
      toast.success('Account created! Starting onboarding...');
      await refreshMembership();
      navigate('/onboarding', { replace: true });
    }
  };

  // Step 2: Google signup
  const signUpWithGoogle = async () => {
    if (!orgInfo) return;

    setGoogleLoading(true);
    localStorage.setItem(PENDING_SIGNUP_KEY, JSON.stringify({
      org_id: orgInfo.org_id,
      department,
    }));

    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/signup?complete=google`,
      },
    });

    if (error) {
      toast.error('Failed to connect with Google. Please try again.');
      localStorage.removeItem(PENDING_SIGNUP_KEY);
      setGoogleLoading(false);
    }
  };

  if (loading || completingGoogle) {
    return (
      <div className="min-h-screen cosmic-bg flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          {completingGoogle && (
            <p className="text-sm text-muted-foreground">Setting up your account...</p>
          )}
        </div>
      </div>
    );
  }

  // Step 1: Invite code + department
  if (!orgInfo) {
    return (
      <div className="min-h-screen cosmic-bg flex items-center justify-center p-6">
        <Card className="w-full max-w-md bg-card/80 backdrop-blur-sm border-border/50">
          <CardHeader className="text-center">
            <div className="mx-auto">
              <img src={logo} alt="Cosmo" className="h-[70px] w-[70px] object-contain" />
            </div>
            <CardTitle className="text-2xl font-display">Join Your Team</CardTitle>
            <CardDescription>Enter your invite code to get started</CardDescription>
          </CardHeader>
          <form onSubmit={handleValidateCode}>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="inviteCode">Invite Code</Label>
                <Input
                  id="inviteCode"
                  type="text"
                  placeholder="ACME2024"
                  value={inviteCode}
                  onChange={e => setInviteCode(e.target.value.toUpperCase())}
                  required
                  className="bg-input uppercase"
                />
                <p className="text-xs text-muted-foreground">
                  Get this from your organization admin
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="department">Department</Label>
                <Select value={department} onValueChange={setDepartment} required>
                  <SelectTrigger className="bg-input">
                    <SelectValue placeholder="Select your department" />
                  </SelectTrigger>
                  <SelectContent>
                    {DEPARTMENTS.map(dept => (
                      <SelectItem key={dept} value={dept}>{dept}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
            <CardFooter className="flex flex-col space-y-4">
              <Button type="submit" className="w-full" disabled={validatingCode || !inviteCode.trim() || !department}>
                {validatingCode ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Verifying...
                  </>
                ) : 'Continue'}
              </Button>
              <p className="text-sm text-muted-foreground text-center">
                Already have an account?{' '}
                <Link to="/login" className="text-primary hover:underline">
                  Sign in
                </Link>
              </p>
            </CardFooter>
          </form>
        </Card>
      </div>
    );
  }

  // Step 2: Choose auth method
  return (
    <div className="min-h-screen cosmic-bg flex items-center justify-center p-6">
      <Card className="w-full max-w-md bg-card/80 backdrop-blur-sm border-border/50">
        <CardHeader className="text-center">
          <div className="mx-auto">
            <img src={logo} alt="Cosmo" className="h-[70px] w-[70px] object-contain" />
          </div>
          <CardTitle className="text-2xl font-display">Create Account</CardTitle>
          <CardDescription>
            Joining <span className="font-medium text-foreground">{orgInfo.org_name}</span>
          </CardDescription>
        </CardHeader>
        <form onSubmit={handleSubmit}>
          <CardContent className="space-y-4">
            <Button
              type="button"
              variant="outline"
              className="w-full"
              onClick={signUpWithGoogle}
              disabled={isLoading || googleLoading}
            >
              {googleLoading ? 'Redirecting...' : (
                <>
                  <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                  </svg>
                  Continue with Google
                </>
              )}
            </Button>

            <div className="relative">
              <Separator />
              <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-card px-2 text-xs text-muted-foreground">
                or
              </span>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="firstName">First Name</Label>
                <Input id="firstName" type="text" placeholder="Jane" value={firstName} onChange={e => setFirstName(e.target.value)} required className="bg-input" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="lastName">Last Name</Label>
                <Input id="lastName" type="text" placeholder="Smith" value={lastName} onChange={e => setLastName(e.target.value)} required className="bg-input" />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" placeholder="you@company.com" value={email} onChange={e => setEmail(e.target.value)} required className="bg-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" placeholder="••••••••" value={password} onChange={e => setPassword(e.target.value)} required className="bg-input" />
            </div>
            <div className="flex items-center justify-between p-3 rounded-lg border border-border bg-muted/30">
              <div className="flex items-center gap-3">
                <Moon className="h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="text-sm font-medium text-foreground">Appearance</p>
                  <p className="text-xs text-muted-foreground">Dark mode</p>
                </div>
              </div>
              <Switch
                checked={theme === 'dark'}
                onCheckedChange={(checked) => setTheme(checked ? 'dark' : 'light')}
              />
            </div>
          </CardContent>
          <CardFooter className="flex flex-col space-y-4">
            <Button type="submit" className="w-full" disabled={isLoading || googleLoading}>
              {isLoading ? 'Creating account...' : 'Create Account'}
            </Button>
            <div className="flex items-center justify-between w-full">
              <button
                type="button"
                onClick={() => setOrgInfo(null)}
                className="text-sm text-muted-foreground hover:text-foreground transition-colors inline-flex items-center gap-1"
              >
                <ArrowLeft className="h-3 w-3" />
                Back
              </button>
              <p className="text-sm text-muted-foreground">
                Already have an account?{' '}
                <Link to="/login" className="text-primary hover:underline">
                  Sign in
                </Link>
              </p>
            </div>
          </CardFooter>
        </form>
      </Card>
    </div>
  );
}
